// `avatar init <workspace-name>` — v3 THIN INIT.
//
// 1 flow duy nhất (bỏ internal/client/library + existing-remote/folder/new-project).
// Init chỉ dựng MÔI TRƯỜNG: login NAL → tạo workspace folder (KHÔNG git) → cài
// team-ai-pack tarball → symlink + settings → GitNexus(install+MCP) → AI setup.
//
// Repo code cài SAU bằng `avatar add repo` (src/ rỗng sau init).
import { resolve } from "node:path";
import type { Command } from "commander";
import { printAvatarBanner } from "../lib/avatar-ascii-banner.js";
import { checkCliVersionGate } from "../lib/check-cli-version-gate.js";
import {
  type CommSetupFlags,
  registerCommSetupFlags,
} from "../lib/comm-setup-non-interactive-flags.js";
import { ensureValidTokenOrLogin } from "../lib/ensure-valid-token-or-login.js";
import { RemoteAccessAbortedError } from "../lib/handle-remote-access-failure-with-account-switch.js";
import { isNonInteractiveFatalError } from "../lib/non-interactive-abort-error.js";
import { isNonInteractive, MissingFlagError } from "../lib/non-interactive-context.js";
import { inputOrFlag } from "../lib/prompt-or-flag.js";
import { UserAbortedRecoveryError } from "../lib/prompt-recovery-action-on-failure.js";
import { InitAbortedByUserError } from "../lib/safe-bootstrap-for-dirty-folder.js";
import { TeamPackAccessAbortedError } from "../lib/team-pack-manager.js";
import { log } from "../lib/terminal-logger.js";
import { readUserConfig } from "../lib/user-config-store.js";
import { promptTeamOwner, resolveWorkspacePath } from "./init-success-rendering-and-helpers.js";
import { scaffoldThinWorkspace } from "./workspace-scaffold-and-finalize-orchestrator.js";

const WORKSPACE_NAME_ARG_SPEC = {
  flag: "<workspace-name>",
  command: "init",
  hint: "avatar init <ten-workspace>",
};

export interface InitOptions extends CommSetupFlags {
  workspaceParent?: string;
  packVersion?: string;
  teamOwner?: string;
  description?: string;
  skipTeamPack?: boolean;
  skipUpdateCheck?: boolean;
  force?: boolean;
  yes?: boolean;
  aiSkip?: boolean;
  gitnexusSkip?: boolean;
  commSkip?: boolean;
}

export function registerInitCommand(program: Command): void {
  const cmd = program
    .command("init")
    .description("Khởi tạo Avatar workspace (môi trường) — repo cài sau bằng 'avatar add repo'")
    .argument("[workspace-name]", "Tên workspace (bắt buộc, hỏi nếu thiếu)")
    .option("--workspace-parent <path>", "Thư mục cha tạo workspace (mặc định . — CWD)")
    .option("--pack-version <tag>", "Pin team-ai-pack vào tag cụ thể (vd v0.2.0-beta.0)")
    .option("--team-owner <email>", "Email team owner (bỏ qua prompt)")
    .option("--description <text>", "Mô tả 1 dòng của workspace")
    .option("--skip-team-pack", "Bỏ qua tải team-ai-pack (test mode)")
    .option("--skip-update-check", "Bỏ qua kiểm tra phiên bản CLI mới")
    .option("--force", "Bỏ qua prompt khi workspace path đã tồn tại")
    .option("--yes", "Auto-confirm tất cả prompt")
    .option("--ai-skip", "Bỏ qua phase AI setup (chạy 'avatar ai setup' sau)")
    .option("--gitnexus-skip", "Bỏ qua phase GitNexus setup")
    .option("--comm-skip", "Bỏ qua phase Setup Chatops (chạy 'avatar chatops-connect' sau)")
    .option(
      "--on-workspace-path-conflict <abort|use-alt>",
      "Xử lý khi thư mục workspace đã có nội dung (mặc định abort)",
    );
  registerCommSetupFlags(cmd, { withProvider: true });
  cmd.action(async (workspaceName: string | undefined, opts: InitOptions) => {
    try {
      await runInit(workspaceName, opts);
    } catch (err) {
      // Thiếu cờ đi tiếp lên `index.ts` để in JSON 1 dòng — đây là vế thứ hai
      // của lỗ L9: chặn ở orchestrator xong mà nuốt lại ở đây thì vẫn mất lỗi.
      if (isNonInteractiveFatalError(err)) throw err;
      if (
        err instanceof InitAbortedByUserError ||
        err instanceof TeamPackAccessAbortedError ||
        err instanceof RemoteAccessAbortedError ||
        err instanceof UserAbortedRecoveryError
      ) {
        log.dim(err.message);
        process.exit(0);
      }
      log.error(err instanceof Error ? err.message : String(err));
      process.exit(1);
    }
  });
}

async function runInit(workspaceName: string | undefined, opts: InitOptions): Promise<void> {
  // Version gate — kiểm tra TRƯỚC mọi bước nặng kể cả banner.
  // Gate trước banner: nếu outdated thì exit gọn, không in banner thừa.
  if (!opts.skipUpdateCheck) {
    const gate = checkCliVersionGate();
    if (gate.outdated) {
      // Non-interactive (F-18 R8b, câu treo 4b = X): không ai đọc 3 dòng warn, và
      // exit 0 làm máy gọi tưởng init xong ⇒ ném lỗi thiếu cờ, lên JSON stderr ≠0.
      if (isNonInteractive()) {
        throw new MissingFlagError({
          missingFlag: "--skip-update-check",
          command: "init",
          hint: "avatar update hoặc thêm --skip-update-check",
        });
      }
      log.warn(`Avatar CLI có bản mới: ${gate.current} → ${gate.latest}.`);
      log.warn("Chạy: avatar update  (rồi chạy lại avatar init).");
      log.warn("Bỏ qua kiểm tra: avatar init --skip-update-check");
      process.exit(0);
    } else if (gate.latest === null) {
      // Offline / npm lỗi — fail-soft, tiếp tục init.
      log.dim("Không kiểm tra được phiên bản CLI (offline?) — tiếp tục init.");
    }
    // Up-to-date → im lặng.
  }

  if (!opts.yes) printAvatarBanner({ tagline: "Khởi tạo Avatar workspace" });

  // Login NAL — bắt buộc (cần token để tải pack từ Supabase). Dùng chung
  // ensureValidTokenOrLogin() với `sync` (force login khi token chết).
  try {
    // `allowLoginWhenNonInteractive`: extension chạy `init --non-interactive` cho người chưa
    // login và dựa vào device flow ở đây — gác của force update (D5) chỉ áp cho `sync`.
    await ensureValidTokenOrLogin({ command: "init", allowLoginWhenNonInteractive: true });
  } catch (err) {
    // Lỗi THẬT (network, OAuth fail) → re-throw để action() exit 1, KHÔNG nuốt
    // thành UserAbortedRecoveryError (exit 0). CI/orchestrator đọc exit code
    // phải thấy init thất bại. UserAbortedRecoveryError (user chủ động bỏ) thì
    // để propagate nguyên trạng → exit 0.
    if (err instanceof UserAbortedRecoveryError) throw err;
    log.error(`Login thất bại: ${(err as Error).message}`);
    throw err;
  }
  const userConfig = await readUserConfig();
  if (!userConfig) {
    throw new UserAbortedRecoveryError(
      "Tải team-ai-pack cần account thuộc workspace NAL — login mở cho mọi account, nhưng pack thì không. Chạy 'avatar login' bằng account NAL rồi 'avatar init' lại.",
    );
  }

  // Tên workspace bắt buộc — hỏi nếu thiếu.
  const name =
    workspaceName ??
    (await inputOrFlag(undefined, WORKSPACE_NAME_ARG_SPEC, {
      message: "Tên workspace:",
      validate: (v) => (v.trim().length > 0 ? true : "Tên không được rỗng"),
    }));

  const teamOwner = opts.teamOwner ?? (await promptTeamOwner(userConfig.email));
  const workspaceParent = resolve(opts.workspaceParent ?? ".");
  const workspacePath = await resolveWorkspacePath(workspaceParent, name.trim(), opts.force);

  await scaffoldThinWorkspace({
    workspacePath,
    workspaceName: name.trim(),
    teamOwner,
    description: opts.description ?? `Avatar workspace: ${name.trim()}`,
    packVersion: opts.packVersion,
    autoYes: opts.yes,
    skipTeamPack: opts.skipTeamPack,
    aiSkip: opts.aiSkip,
    gitnexusSkip: opts.gitnexusSkip,
    commSkip: opts.commSkip,
    commFlags: opts,
  });
}
